import * as microsoftTeams from '@microsoft/teams-js';
import { AppChannelMessages, Logger } from '@hoylu/login-common'
import debug from 'debug';
import { v4 } from 'uuid';
import { Metrics } from './metrics';
import { request } from './request';
import { createIframe } from './appContainers/createIframe';
import { removeIframe } from './appContainers/removeIframe';
import { createPopup } from './appContainers/createPopup';
import { teamsPopup } from './appContainers/teamsPopup';
import { promiseFailure, promiseCallback } from './types';
import { createPromiseWithMetrics } from './createPromiseWithMetrics';
export const trace = debug('login-lib');

type Token = string;
export type LoginType = 'Basic' | 'AzureAD' | 'Okta';
export type HoyluAuthResponse = {
  token: Token;
  loginType?: LoginType
}

function sessionId() {
  return v4()
}

export class Auth {
  private loginHost: string;
  private authHost: string;
  private google_enabled: boolean;
  private timeout: number;
  private appNameEncoded: string;
  private appVersionEncoded: string;
  private metrics?: Metrics;
  private log?: Logger;

  constructor(options: {
    loginHost: string;
    authHost?: string;
    metricsEndpoint?: string;
    metricsLabels?: { [key: string]: string | number };
    google_enabled?: boolean;
    appName?: string;
    appVersion?: string,
    logger?: Logger
  }) {
    if (!options.loginHost) throw new Error('missing option login host');
    if (options.metricsEndpoint) {
      const metricLabels = {
        appName: options.appName || 'unknown',
        appVersion: options.appVersion || 'unknown',
        appHost: window.location.host,
        userAgent: window.navigator.userAgent,
        ...options.metricsLabels
      }
      this.metrics = new Metrics(options.metricsEndpoint, metricLabels)
    }
    this.log = options.logger;
    this.loginHost = options.loginHost;
    this.authHost = options.authHost || '';
    this.google_enabled = options.google_enabled || false;
    this.timeout = 40000;
    this.appNameEncoded = encodeURIComponent(options.appName ?? "");
    this.appVersionEncoded = encodeURIComponent(options.appVersion ?? "");
  }

  validateToken(token: Token) {
    return createPromiseWithMetrics<Token>(this.metrics?.validateTokenMetrics(), async (resolve, reject) => {
      var traceData = { source: 'login-lib', operation: 'validateToken' }
      if (!token) {
        this.log?.warn('no token provided - rejecting', traceData)
        reject('no token provided')
      }
      if (!this.authHost) {
        this.log?.warn('auth host not defined - rejecting', traceData)
        reject('auth host not defined')
      }
      try {
        await jsonRequest(
          'get',
          token,
          `${this.authHost}/validate`
        );
        this.log?.info('token is valid', traceData)
        resolve(token);
      } catch (err) {
        this.log?.info('token is not valid', traceData)
        reject('invalid token');
      }
    });
  }

  loginRedirect(redirect_uri: string) {
    window.location.replace(
      `${this.loginHost}?redirect_uri=${redirect_uri}&${this.metaDataQueryString}${this.google_enabled ? '&google_enabled=true' : ''}`
    );
  }

  login() {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.loginMetrics(), (resolve, reject) => {
      if (!window) return reject('no browser window');
      createPopup(
        this.loginHost,
        "?" + this.metaDataQueryString + (this.google_enabled ? '&google_enabled=true' : ''),
        resolve,
        reject
      );
    });
  }

  teamsLogin(HostClientType: microsoftTeams.HostClientType) {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.loginMetrics({ appContext: "teams" }), (resolve, reject) => {
      if (HostClientType === 'desktop') {
        microsoftTeams.initialize(() => {
          teamsPopup(`${this.loginHost}?teams=true&${this.metaDataQueryString}`, resolve, reject);
        });
      } else {
        throw new Error('teams login called with wrong context');
      }
    });
  }

  getTokenSilent(forceRefresh?: boolean, currentLoginState: 'LOGGED_IN' | 'NOT_LOGGED_IN' | 'UNKNOWN' = "UNKNOWN") {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.getTokenSilentMetrics(), (resolve, reject) => {
      var traceData = { source: 'login-lib', operation: 'getTokenSilent', "data.currentLoginState": currentLoginState }
      if (!document) {
        this.log?.error('no browser window', traceData)
        return reject('no browser window')
      }
      let loginAppIframe: HTMLIFrameElement | undefined;
      if (forceRefresh) {
        this.log?.info('forcing refresh', traceData)
        loginAppIframe = createIframe(
          `${this.loginHost}/refresh?${this.metaDataQueryString}`,
          'hidden-login-refresh',
          this.log
        );
      } else {
        this.log?.info('doing silent refresh', traceData)
        loginAppIframe = createIframe(
          `${this.loginHost}/silent?${this.metaDataQueryString}`,
          'hidden-login-refresh',
          this.log
        );
      }
      if (!loginAppIframe) {
        this.log?.error('request in progress', traceData)
        return reject('request in progress')
      }
      loginAppIframe.onerror = (e) => {
        console.error(e);
        this.log?.error('resource failed to load', traceData)
        return reject('resource failed to load');
      }

      const timer = setInterval(() => {
        removeIframe('hidden-login-refresh');
        clearInterval(timer);
        this.log?.error('request timeout', traceData)
        return reject('request timeout');
      }, this.timeout);

      const messageHandler = (e: MessageEvent) => {
        trace('got message', e)
        if (this.loginHost !== e.origin) return;

        if (e.data.status) {
          this.log?.info(`got ${e.data.status} result`, traceData)
          removeIframe('hidden-login-refresh');
          clearInterval(timer);
          this.log?.info('returning result', { status: e.data.status, ...traceData })
          resolveResponse(e.data, resolve, reject, this.log);
          window.removeEventListener('message', messageHandler);
        } else if (e.data === AppChannelMessages.APP_READY_MESSAGE) {
          this.log?.info('got APP_READY_MESSAGE', traceData)
          loginAppIframe?.contentWindow?.postMessage(AppChannelMessages.TARGET_ORIGIN_MESSAGE, this.loginHost);
        }
      }
      window.addEventListener('message', messageHandler);
    });
  }

  registerEmail(email: string) {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.registerEmailMetrics(), (resolve, reject) => {
      if (!window) return reject('no browser window');
      createPopup(
        this.loginHost,
        `/registerEmail?email=${email}&${this.metaDataQueryString}`,
        resolve,
        reject
      );
    });
  }

  showCompleteRegistrationForm(
    email: string,
    targetElement: HTMLElement,
    completionCallback: (token: Token) => void
  ) {
    let frame = targetElement.appendChild(document.createElement('iframe'));
    frame.src = `${this.loginHost}/completeRegistration?email=${email}&${this.metaDataQueryString}`;
    frame.style.width = '397px';
    frame.style.height = '590px';

    window.addEventListener('message', (e: MessageEvent) => {
      if (this.loginHost !== e.origin) return;

      if (e.data.status) {
        targetElement.removeChild(frame);
        completionCallback(e.data.result.token);
      } else if (e.data === AppChannelMessages.APP_READY_MESSAGE) {
        frame.contentWindow?.postMessage(AppChannelMessages.TARGET_ORIGIN_MESSAGE, this.loginHost);
      }
    });
  }

  teamsRegisterEmail(
    HostClientType: microsoftTeams.HostClientType,
    email: string
  ) {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.registerEmailMetrics({ appContext: "teams" }), (resolve, reject) => {
      if (HostClientType === 'desktop') {
        microsoftTeams.initialize(() => {
          teamsPopup(
            `${this.loginHost}/registerEmail?email=${email}&teams=true&${this.metaDataQueryString}`,
            resolve,
            reject
          );
        });
      } else {
        throw new Error('teams register email called with wrong context');
      }
    });
  }

  logout() {
    return createPromiseWithMetrics<HoyluAuthResponse>(this.metrics?.logoutMetrics(), (resolve, reject) => {
      if (!document) return reject('no browser window');
      let loginAppIframe = createIframe(
        `${this.loginHost}/logout?${this.metaDataQueryString}`,
        'hidden-login-logout',
        this.log
      );
      if (!loginAppIframe) return reject('request in progress');
      loginAppIframe.onerror = (e) => {
        console.error(e);
        return reject('resource failed to load');
      }

      const timer = setInterval(() => {
        removeIframe('hidden-login-logout');
        clearInterval(timer);
        return reject('request timeout');
      }, this.timeout);

      window.addEventListener('message', (e: MessageEvent) => {
        if (this.loginHost !== e.origin) return;

        if (e.data.status) {
          removeIframe('hidden-login-logout');
          clearInterval(timer);
          resolveResponse(e.data, resolve, reject, this.log);
        } else if (e.data === AppChannelMessages.APP_READY_MESSAGE) {
          loginAppIframe?.contentWindow?.postMessage(AppChannelMessages.TARGET_ORIGIN_MESSAGE, this.loginHost);
        }
      });
    });
  }

  private get metaDataQueryString() {
    return `appName=${this.appNameEncoded}&appVersion=${this.appVersionEncoded}&sessionId=${this.log?.getSessionId()}`;
  }
}

export default Auth;

export function resolveResponse(
  data: { status: 'SUCCESS' | 'FAILURE'; result: any },
  resolve: promiseCallback,
  reject: promiseFailure,
  log?: Logger
) {
  log?.info('resolving response:')
  switch (data.status) {
    case 'SUCCESS':
      log?.info('  SUCCESS')
      resolve(data.result);
      break;
    case 'FAILURE':
      log?.info('  FAILURE - rejecting')
      reject(data.result);
      break;
    default:
      log?.info('  unknown - rejecting')
      reject(`unknown response status: ${data.status}`);
  }
}

async function jsonRequest(
  method: string,
  auth: string | null,
  endpoint: string
) {
  let response = await request(method, auth, endpoint);
  return response.json();
}
